import {
	buildDownloadUrl,
	DOWNLOAD_FETCH_OPTIONS,
	downloadFile,
	extractFilenameFromUrl,
	saveBlobAsFile,
	withBlobExtension
} from "@common-ui/media/downloadFile";
import JSZip from "jszip";
import moment from "moment-timezone";
import timezones from "@/helpers/timezones";
import { ArtifactType } from "@/enums/ArtifactType";
import DeviceUtility from "./device";
import { SelectedMediaItem } from "@/types/media/SelectedMediaItem";
import { dateKeyToFolderName } from "@/helpers/dayView";

export { downloadFile } from "@common-ui/media/downloadFile";

/** The lowercase extension of a URL's last path segment ("" when it has none), ignoring query and fragment. */
function extensionFromUrlPath(url: string): string {
	const lastPathSegment = url.split(/[?#]/)[0].split("/").pop() ?? "";
	const dotIndex = lastPathSegment.lastIndexOf(".");
	const candidate = dotIndex > 0 ? lastPathSegment.slice(dotIndex + 1).toLowerCase() : "";
	return /^[a-z0-9]{1,8}$/.test(candidate) ? candidate : "";
}

function assertMediaUrls(items: SelectedMediaItem[]) {
	const itemWithoutUrl = items.find((item) => !item.mediaUrl?.trim());
	if (itemWithoutUrl) {
		throw new Error(`Selected media item ${itemWithoutUrl.id} is missing a media URL`);
	}
}

function reserveUniqueFileName(fileName: string, usedFileNames: Set<string>) {
	const extensionIndex = fileName.lastIndexOf(".");
	const baseName = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
	const extension = extensionIndex > 0 ? fileName.slice(extensionIndex) : "";
	let uniqueFileName = fileName;
	let suffix = 2;

	while (usedFileNames.has(uniqueFileName.toLowerCase())) {
		uniqueFileName = `${baseName} (${suffix})${extension}`;
		suffix += 1;
	}

	usedFileNames.add(uniqueFileName.toLowerCase());
	return uniqueFileName;
}

async function waitForEveryDownload(downloads: Promise<void>[]) {
	const results = await Promise.allSettled(downloads);
	const failedDownload = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
	if (failedDownload) {
		throw failedDownload.reason;
	}
}

export function triggerBlobDownload(blob: Blob, fileName: string) {
	saveBlobAsFile(blob, fileName);
}

/**
 * Download file(s).
 * @param {Array} artifactUrls - An array of URLs to download.
 * @param {Array} artifactTitles - An array of name(s) to save the downloaded file(s) as.
 * @param {string} sessionOrProjectName - The name of the session or project.
 */
export async function downloadFiles(artifactUrls: string[], artifactTitles: string[], sessionOrProjectName = "") {
	if (artifactUrls.length == 0 || artifactTitles.length == 0) {
		return;
	}
	if (artifactUrls.length == 1) {
		return await downloadFile(artifactUrls[0], artifactTitles[0]);
	}
	const zip = new JSZip();
	const datePart = timezones.formatDate(new Date().toISOString(), "YYYY-MM-DD_HHmmss");
	sessionOrProjectName = sessionOrProjectName.replace(/\s+/g, "-");
	sessionOrProjectName = sessionOrProjectName.replace(/[^a-zA-Z0-9-_]/g, "");
	const folderName = ("files" + "_" + sessionOrProjectName.substring(0, 25) + "_" + datePart).toLowerCase();
	const folder: JSZip | null = zip.folder(folderName);
	const usedFileNames = new Set<string>();

	const downloadFileIntoFolder = async (url: string, fileName: string) => {
		const fileUrl = buildDownloadUrl(url);
		if (!fileName) {
			fileName = extractFilenameFromUrl(url); //fallback to extracted filename
		}

		try {
			const response = await fetch(fileUrl, DOWNLOAD_FETCH_OPTIONS);
			if (!response.ok) {
				throw new Error(`Failed to fetch ${fileUrl}: ${response.statusText}`);
			}
			const blob = await response.blob();
			folder?.file(reserveUniqueFileName(withBlobExtension(url, fileName, blob), usedFileNames), blob);
		} catch (error) {
			console.error(`Failed to download file from ${fileUrl}:`, error);
			console.error(error, {
				tags: {
					method: "downloadAsZip",
					file: "download.js"
				},
				extra: {
					fileUrl: fileUrl
				}
			});
		}
	};

	const downloadPromises = artifactUrls.map((url: string, index: number) => {
		const fileName = artifactTitles[index];
		return downloadFileIntoFolder(url, fileName);
	});

	await Promise.all(downloadPromises);

	const content = await zip.generateAsync({ type: "blob" });
	triggerBlobDownload(content, `${folderName}.zip`);
}

export async function downloadMediaFiles(selectedMediaItems: SelectedMediaItem[], parentId: string = "", parentName: string = "") {
	if (selectedMediaItems.length == 0) {
		return;
	}

	assertMediaUrls(selectedMediaItems);

	// Download a single file instead of zipping

	if (selectedMediaItems.length == 1) {
		const item = selectedMediaItems[0];
		const fileName = generateFileName(item.type, { id: item.id, src: item.mediaUrl, name: item.name }, parentId ?? item.conversationId);
		return await downloadFile(selectedMediaItems[0].mediaUrl, fileName, { failOnRejectedResponse: true });
	}

	const zip = new JSZip();
	const datePart = timezones.formatDate(new Date().toISOString(), "YYYY-MM-DD_HHmmss");
	parentName = parentName.replace(/\s+/g, "-");
	parentName = parentName.replace(/[^a-zA-Z0-9-_]/g, "");
	const folderName = ("files" + "_" + parentName.substring(0, 25) + "_" + datePart).toLowerCase();
	const folder: JSZip | null = zip.folder(folderName);
	const usedFileNames = new Set<string>();

	const downloadFileIntoFolder = async (url: string, fileName: string, item: SelectedMediaItem) => {
		const fileUrl = buildDownloadUrl(url);
		if (!fileName) {
			fileName = generateFileName(item.type, { id: item.id, src: item.mediaUrl, name: item.name }, parentId ?? item.conversationId);
		}

		try {
			const response = await fetch(fileUrl, DOWNLOAD_FETCH_OPTIONS);
			if (!response.ok) {
				throw new Error(`Failed to fetch ${fileUrl}: ${response.statusText}`);
			}
			const blob = await response.blob();
			folder?.file(reserveUniqueFileName(withBlobExtension(url, fileName, blob), usedFileNames), blob);
		} catch (error) {
			console.error(`Failed to download file from ${fileUrl}:`, error);
			console.error(error, {
				tags: {
					method: "downloadAsZip",
					file: "download.js"
				},
				extra: {
					fileUrl: fileUrl
				}
			});
			throw error;
		}
	};

	const downloadPromises = selectedMediaItems.map((item: SelectedMediaItem) => {
		let fileName = item.name;

		if (!fileName) {
			fileName = generateFileName(item.type, { id: item.id, src: item.mediaUrl, name: item.name }, parentId ?? item.conversationId);
		}

		return downloadFileIntoFolder(item.mediaUrl, fileName, item);
	});

	await waitForEveryDownload(downloadPromises);

	const content = await zip.generateAsync({ type: "blob" });
	triggerBlobDownload(content, `${folderName}.zip`);
}

/**
 * Download a file from a given URL and save it with the specified file name.
 * @param {string} url - The URL of the file to download.
 * @param {string} fileName - The name to save the downloaded file as.
 * @param options - Optional download behavior.
 * @returns Whether the download was started successfully.
 */

/**
 * Generate a file name.
 * @param {string} artifactType - The type of artifact (e.g., "files", "snapshots", "recordings").
 * @param {Object} artifact - The artifact object containing the file information.
 * @param {string} parentId - The ID of the parent session or project.
 * @param {string} extension - The file extension (default is "mp4").
 */
export function generateFileName(artifactType: ArtifactType, artifact: any, parentId: string, extension = "mp4") {
	// LSP-16659: documents are downloadable from the media grid now. A file keeps the name the person
	// uploaded it under (the caller passes the display/download name), and otherwise takes its real
	// extension from the signed URL — never the recording default "mp4".
	if (artifactType === ArtifactType.Files) {
		const realName = typeof artifact?.name === "string" ? artifact.name.trim() : "";
		if (realName) return realName;
		const urlExtension = extensionFromUrlPath(String(artifact?.src ?? ""));
		const baseName = parentId ? `file.${parentId}.${artifact.id}` : `file.${artifact.id}`;
		return urlExtension ? `${baseName}.${urlExtension}` : baseName;
	}
	if (artifactType === ArtifactType.Snapshots) {
		const fileName = artifact.src.split("?")[0];
		extension = fileName.startsWith("blob:") ? "jpg" : fileName.split(".").pop();
	}

	if (parentId) {
		return `file.${parentId}.${artifact.id}.${extension}`;
	} else {
		return `file.${artifact.id}.${extension}`;
	}
}

/**
 * Process and Download selected artifacts from a list of artifacts. Use it when you have to generate urls and titles first from a list of artifacts.
 * @param {string} artifactType - The type of artifact (e.g., "files", "snapshots", "recordings").
 * @param {string} parentName - The name of the parent session or project.
 * @param {string} parentId - The ID of the parent session or project.
 * @param {Array} artifacts - The list of artifacts.
 * @param {Array} selectedIds - The IDs of the selected artifacts.
 */
export async function processArtifactsAndDownload(
	artifactType: ArtifactType,
	parentName: string,
	parentId: string,
	artifacts: any[],
	selectedIds: string[]
) {
	try {
		if (!artifacts || artifacts.length === 0 || selectedIds.length === 0) {
			return;
		}
		const artifactUrls = artifacts.filter((artifact) => selectedIds.includes(artifact.id)).map((artifact) => artifact.src);

		let artifactTitles = [];

		//files have titles by default that we need to preserve while downloading
		if (artifactType === ArtifactType.Files) {
			artifactTitles = artifacts.filter((artifact) => selectedIds.includes(artifact.id)).map((artifact) => artifact.title);
		} else {
			artifactTitles = artifacts
				.filter((artifact) => selectedIds.includes(artifact.id))
				.map((artifact) => generateFileName(artifactType, artifact, parentId));
		}
		await downloadFiles(artifactUrls, artifactTitles, parentName);
	} catch (err) {
		//caught in the downloadFiles function
	}
}

/***
 * Download an image from a canvas element.
 * @param {HTMLCanvasElement} canvas - The canvas element to download.
 * @param {string} fileName - The name of the file to save the image as (default is "canvas-image.png").
 */
export async function downloadFromCanvas(canvas: HTMLCanvasElement, fileName: string = "canvas-image.png") {
	const link = document.createElement("a");
	link.download = fileName;
	if (DeviceUtility.isFirefox) {
		const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve));
		if (!blob) {
			throw new Error("Blob not created");
		}
		const url = URL.createObjectURL(blob);
		link.href = url;
	} else {
		link.href = canvas.toDataURL("image/png");
	}

	link.click();
}

/**
 * Generate a file name for a video artifact.
 * @param artifactType - The type of artifact (e.g., "files", "snapshots", "recordings").
 * @param artifactId - The ID of the artifact.
 * @param artifactUrl - The URL of the artifact.
 * @param parentId - The ID of the parent session or project.
 * @returns The file name for the video artifact.
 */
export function videoFileName(artifactType: ArtifactType, artifactId: string, artifactUrl: string, parentId: string) {
	return generateFileName(artifactType, { id: artifactId, src: artifactUrl }, parentId);
}

/**
 * Download media files grouped by date into a zip with mm-dd-yyyy folder structure.
 * Each item must include a `created` date string.
 * @param items - Selected media items with a `created` date string.
 * @param parentId - The ID of the parent session or project.
 * @param parentName - The name of the parent session or project.
 */
export async function downloadMediaFilesByDate(
	items: (SelectedMediaItem & { created: string })[],
	parentId: string = "",
	parentName: string = ""
) {
	if (!items || items.length === 0) {
		return;
	}

	assertMediaUrls(items);

	const zip = new JSZip();
	const datePart = timezones.formatDate(new Date().toISOString(), "YYYY-MM-DD_HHmmss");
	parentName = parentName.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-_]/g, "");
	const rootFolderName = ("files" + "_" + parentName.substring(0, 25) + "_" + datePart).toLowerCase();
	const rootFolder: JSZip | null = zip.folder(rootFolderName);

	// Group items by date — items without a valid date go into the root folder
	const tz = moment.tz.guess();
	const dateGroups = new Map<string, (SelectedMediaItem & { created: string })[]>();
	const undatedItems: (SelectedMediaItem & { created: string })[] = [];

	for (const item of items) {
		if (!item.created) {
			undatedItems.push(item);
			continue;
		}
		const m = moment.tz(item.created, tz);
		if (!m.isValid()) {
			undatedItems.push(item);
			continue;
		}
		const dateKey = m.format("YYYY-MM-DD");

		if (!dateGroups.has(dateKey)) {
			dateGroups.set(dateKey, []);
		}
		dateGroups.get(dateKey)!.push(item);
	}

	const downloadFileIntoFolder = async (folder: JSZip | null, url: string, fileName: string, usedFileNames: Set<string>) => {
		const fileUrl = buildDownloadUrl(url);

		try {
			const response = await fetch(fileUrl, DOWNLOAD_FETCH_OPTIONS);
			if (!response.ok) {
				throw new Error(`Failed to fetch ${fileUrl}: ${response.statusText}`);
			}
			const blob = await response.blob();
			folder?.file(reserveUniqueFileName(withBlobExtension(url, fileName, blob), usedFileNames), blob);
		} catch (error) {
			console.error(`Failed to download file from ${fileUrl}:`, error);
			throw error;
		}
	};

	const downloadPromises: Promise<void>[] = [];

	for (const [dateKey, groupItems] of dateGroups) {
		const folderName = dateKeyToFolderName(dateKey);
		const dateFolder = rootFolder?.folder(folderName) ?? null;
		const usedFileNames = new Set<string>();

		for (const item of groupItems) {
			let fileName = item.name;
			if (!fileName) {
				fileName = generateFileName(
					item.type,
					{ id: item.id, src: item.mediaUrl, name: item.name },
					parentId ?? item.conversationId
				);
			}
			downloadPromises.push(downloadFileIntoFolder(dateFolder, item.mediaUrl, fileName, usedFileNames));
		}
	}

	const usedUndatedFileNames = new Set<string>();
	for (const item of undatedItems) {
		let fileName = item.name;
		if (!fileName) {
			fileName = generateFileName(item.type, { id: item.id, src: item.mediaUrl, name: item.name }, parentId ?? item.conversationId);
		}
		downloadPromises.push(downloadFileIntoFolder(rootFolder, item.mediaUrl, fileName, usedUndatedFileNames));
	}

	await waitForEveryDownload(downloadPromises);

	const content = await zip.generateAsync({ type: "blob" });
	triggerBlobDownload(content, `${rootFolderName}.zip`);
}
